import { useState, useEffect } from 'react'
import { TenantGuard } from '@/components/TenantGuard'
import { useAuth } from '@/hooks/useApi'
import { useTenantContext } from '@/contexts/TenantContext'
import { apiClient } from '@/lib/api'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { DataTable } from '@/components/ui/data-table'
import { MessageSquare, Save, Palette, MapPin, Briefcase, Plus, Edit, Trash2, AlertCircle, Building, Sparkles, Upload as UploadIcon, Clock, RotateCcw } from 'lucide-react'
import { LocationModal } from '@/components/LocationModal'
import { ServiceModal } from '@/components/ServiceModal'
import { QueueConfigTab } from '@/components/QueueConfigTab'
import { themePackages, themeCategories, ThemePackage } from '@/data/themePackages'

export function SettingsImproved() {
  const { user } = useAuth()
  const { selectedTenantId } = useTenantContext()
  const activeTenantId = selectedTenantId || user?.tenant_from_group
  
  const [settings, setSettings] = useState<{
    disable_sms: boolean
    allow_multi_called_default: boolean
    location_label: string
    branding: {
      company_name?: string
      logo_url?: string
      primary_color?: string
      secondary_color?: string
      admin_background_color?: string
      sms_sender_name?: string
      support_email?: string
      support_phone?: string
      web_join_title?: string
      web_join_subtitle?: string
      web_join_footer?: string
      web_join_background_color?: string
      web_join_text_color?: string
      web_join_button_color?: string
      web_join_success_message?: string
      web_join_error_message?: string
    }
  }>({ 
    disable_sms: false, 
    allow_multi_called_default: false,
    location_label: 'location',
    branding: {}
  })
  const [locations, setLocations] = useState<any[]>([])
  const [services, setServices] = useState<any[]>([])
  const [maxLocations, setMaxLocations] = useState(1)
  const [maxServices, setMaxServices] = useState(5)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [showLocationModal, setShowLocationModal] = useState(false)
  const [showServiceModal, setShowServiceModal] = useState(false)
  const [editingLocation, setEditingLocation] = useState<any>(null)
  const [editingService, setEditingService] = useState<any>(null)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [selectedCategory, setSelectedCategory] = useState('professional')
  const [customMode, setCustomMode] = useState(false)
  const [selectedThemeId, setSelectedThemeId] = useState<string | null>(null)
  const [cognitoClientId, setCognitoClientId] = useState<string>('')
  const [sessionSettings, setSessionSettings] = useState({
    id_token_validity: 60,
    access_token_validity: 60,
    refresh_token_validity: 30
  })
  const [savingSession, setSavingSession] = useState(false)

  const fetchSettings = async () => {
    if (!activeTenantId) return
    
    try {
      setLoading(true)
      const response = await apiClient.getTenantSettings(activeTenantId)
      setSettings({
        disable_sms: response.disable_sms || false,
        allow_multi_called_default: response.allow_multi_called_default || false,
        location_label: response.settings?.location_label || 'location',
        branding: {
          company_name: response.settings?.branding?.company_name || '',
          logo_url: response.settings?.branding?.logo_url || '',
          primary_color: response.settings?.branding?.primary_color || '#3C226E',
          secondary_color: response.settings?.branding?.secondary_color || '#9CFF00',
          admin_background_color: (response.settings?.branding as any)?.admin_background_color || '#F5F5F7',
          sms_sender_name: response.settings?.branding?.sms_sender_name || '',
          support_email: response.settings?.branding?.support_email || '',
          support_phone: response.settings?.branding?.support_phone || '',
          web_join_title: response.settings?.branding?.web_join_title || '',
          web_join_subtitle: response.settings?.branding?.web_join_subtitle || '',
          web_join_footer: response.settings?.branding?.web_join_footer || '',
          web_join_background_color: response.settings?.branding?.web_join_background_color || '#FFFFFF',
          web_join_text_color: response.settings?.branding?.web_join_text_color || '#333333',
          web_join_button_color: response.settings?.branding?.web_join_button_color || '#3C226E',
          web_join_success_message: response.settings?.branding?.web_join_success_message || '',
          web_join_error_message: response.settings?.branding?.web_join_error_message || ''
        }
      })
      
      // Fetch Cognito client ID and session settings
      if (response.cognito_client_id) {
        setCognitoClientId(response.cognito_client_id)
        await fetchSessionSettings(response.cognito_client_id)
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err)
    } finally {
      setLoading(false)
    }
  }
  
  const fetchSessionSettings = async (clientId: string) => {
    try {
      const response = await apiClient.get(`/admin/tenant/${activeTenantId}/session/${clientId}`)
      setSessionSettings({
        id_token_validity: response.id_token_validity || 60,
        access_token_validity: response.access_token_validity || 60,
        refresh_token_validity: response.refresh_token_validity || 30
      })
    } catch (err) {
      console.error('Failed to fetch session settings:', err)
    }
  }
  
  const saveSessionSettings = async () => {
    if (!cognitoClientId || !activeTenantId) return
    
    try {
      setSavingSession(true)
      await apiClient.put(`/admin/tenant/${activeTenantId}/session/${cognitoClientId}`, sessionSettings)
      alert('Session settings saved successfully!')
    } catch (err: any) {
      alert(err.message || 'Failed to save session settings')
    } finally {
      setSavingSession(false)
    }
  }

  const fetchLocations = async () => {
    if (!activeTenantId) return
    
    try {
      console.log('Fetching locations for tenant:', activeTenantId)
      const response = await apiClient.getLocations(activeTenantId)
      console.log('Locations response:', response)
      setLocations(response.locations || [])
      setMaxLocations(response.max_allowed || 1)
    } catch (err) {
      console.error('Failed to fetch locations:', err)
    }
  }

  const fetchServices = async () => {
    if (!activeTenantId) return
    
    try {
      const response = await apiClient.getServices(activeTenantId)
      setServices(response.services || [])
      setMaxServices(response.max_allowed || 5)
    } catch (err) {
      console.error('Failed to fetch services:', err)
    }
  }

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !activeTenantId) return

    try {
      setSaving(true)
      const formData = new FormData()
      formData.append('file', file)
      
      const result = await apiClient.uploadImage(formData, {
        tenant: activeTenantId,
        location: 'branding',
        service: 'logo'
      })
      
      const updatedBranding = { ...settings.branding, logo_url: result.url }
      
      setSettings(prev => ({
        ...prev,
        branding: updatedBranding
      }))
      
      await apiClient.updateTenantSettings(activeTenantId, {
        settings: {
          branding: updatedBranding
        }
      })
      
      alert('Logo uploaded and saved successfully!')
    } catch (err) {
      console.error('Failed to upload logo:', err)
      alert('Failed to upload logo. Please try again.')
    } finally {
      setSaving(false)
      event.target.value = ''
    }
  }

  const applyThemePackage = (theme: ThemePackage) => {
    setSettings((prev) => ({
      ...prev,
      branding: {
        ...prev.branding,
        primary_color: theme.primary,
        secondary_color: theme.secondary,
        admin_background_color: theme.background
      }
    }))
    setSelectedThemeId(theme.id)
    setCustomMode(false)
  }

  useEffect(() => {
    if (settings.branding) {
      const currentColors = {
        primary: settings.branding.primary_color,
        secondary: settings.branding.secondary_color,
        background: settings.branding.admin_background_color
      }
      
      const matchingTheme = themePackages.find(theme => 
        theme.primary === currentColors.primary &&
        theme.secondary === currentColors.secondary &&
        theme.background === currentColors.background
      )
      
      if (matchingTheme) {
        setSelectedThemeId(matchingTheme.id)
        setCustomMode(false)
      } else {
        setSelectedThemeId(null)
        setCustomMode(true)
      }
    }
  }, [settings.branding?.primary_color, settings.branding?.secondary_color, settings.branding?.admin_background_color])

  useEffect(() => {
    fetchSettings()
    fetchLocations()
    fetchServices()
  }, [activeTenantId])

  const saveSettings = async () => {
    if (!activeTenantId) return
    
    try {
      setSaving(true)
      await apiClient.updateTenantSettings(activeTenantId, {
        disable_sms: settings.disable_sms,
        allow_multi_called_default: settings.allow_multi_called_default,
        settings: {
          location_label: settings.location_label,
          branding: settings.branding
        }
      })
      alert('Settings saved successfully!')
    } catch (err: any) {
      alert(err.message || 'Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteLocation = async (locationId: string) => {
    if (!confirm(`Delete location "${locationId}"?`)) return
    
    try {
      setDeleting(locationId)
      await apiClient.deleteLocation(activeTenantId!, locationId)
      apiClient.clearCache('locations')
      await fetchLocations()
    } catch (err: any) {
      alert(err.message || 'Failed to delete location')
    } finally {
      setDeleting(null)
    }
  }

  const handleDeleteService = async (serviceId: string) => {
    if (!confirm(`Delete service "${serviceId}"?`)) return
    
    try {
      setDeleting(serviceId)
      await apiClient.deleteService(activeTenantId!, serviceId)
      apiClient.clearCache('services')
      await fetchServices()
    } catch (err: any) {
      alert(err.message || 'Failed to delete service')
    } finally {
      setDeleting(null)
    }
  }

  const locationColumns: any[] = [
    { key: 'location_id' as const, header: 'ID' },
    { key: 'name' as const, header: 'Name' },
    { key: 'address' as const, header: 'Address', render: (v: string) => v || '-' },
    { key: 'phone' as const, header: 'Phone', render: (v: string) => v || '-' },
    {
      key: 'actions' as const,
      header: 'Actions',
      render: (_: string, row: any) => (
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { setEditingLocation(row); setShowLocationModal(true) }}>
            <Edit className="h-4 w-4" />
          </Button>
          <Button variant="destructive" size="sm" onClick={() => handleDeleteLocation(row.location_id)} disabled={deleting === row.location_id}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )
    }
  ]

  const serviceColumns: any[] = [
    { key: 'service_id' as const, header: 'ID' },
    { key: 'name' as const, header: 'Name' },
    { key: 'description' as const, header: 'Description', render: (v: string) => v || '-' },
    { key: 'estimated_minutes' as const, header: 'Est. Time (min)' },
    {
      key: 'actions' as const,
      header: 'Actions',
      render: (_: string, row: any) => (
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { setEditingService(row); setShowServiceModal(true) }}>
            <Edit className="h-4 w-4" />
          </Button>
          <Button variant="destructive" size="sm" onClick={() => handleDeleteService(row.service_id)} disabled={deleting === row.service_id}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )
    }
  ]

  const atLocationLimit = locations.length >= maxLocations

  return (
    <TenantGuard>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Settings</h1>
          <p className="text-gray-600 mt-1">Configure your tenant settings and preferences</p>
        </div>

        <Tabs defaultValue="general" className="w-full">
          <TabsList className="grid w-full grid-cols-5 h-12 bg-gray-100 p-1 rounded-lg">
            <TabsTrigger value="general" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">General</TabsTrigger>
            <TabsTrigger value="locations" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">Locations</TabsTrigger>
            <TabsTrigger value="services" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">Services</TabsTrigger>
            <TabsTrigger value="queues" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">Queues</TabsTrigger>
            <TabsTrigger value="branding" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">Branding</TabsTrigger>
          </TabsList>

          {/* General Settings */}
          <TabsContent value="general" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  SMS Settings
                </CardTitle>
                <CardDescription>Configure SMS messaging preferences</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base font-medium">Disable SMS Messages</Label>
                    <p className="text-sm text-gray-500">Temporarily disable all SMS notifications</p>
                  </div>
                  <Switch
                    checked={settings.disable_sms}
                    onCheckedChange={(checked) => setSettings((prev: any) => ({ ...prev, disable_sms: checked }))}
                    disabled={loading}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base font-medium">Allow Multiple Called Customers</Label>
                    <p className="text-sm text-gray-500">Managers can call multiple customers at once</p>
                  </div>
                  <Switch
                    checked={settings.allow_multi_called_default}
                    onCheckedChange={(checked) => setSettings((prev: any) => ({ ...prev, allow_multi_called_default: checked }))}
                    disabled={loading}
                  />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Business Terminology
                </CardTitle>
                <CardDescription>Customize terminology used in SMS messages and interface</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-base font-medium">Location Label</Label>
                  <Input
                    value={settings.location_label}
                    onChange={(e) => setSettings((prev: any) => ({ ...prev, location_label: e.target.value }))}
                    placeholder="location"
                    disabled={loading}
                    className="max-w-xs"
                  />
                  <p className="text-sm text-gray-500">
                    Used in SMS messages like "Select a {settings.location_label || 'location'} to focus". 
                    Examples: "Games", "Table", "Service", "Desk"
                  </p>
                </div>
                
                <div className="pt-6 border-t flex gap-3">
                  <Button onClick={saveSettings} disabled={saving || loading} size="lg" className="min-w-[140px]">
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Settings'}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="lg" 
                    onClick={() => {
                      localStorage.removeItem('qmi_tutorial_completed')
                      window.location.reload()
                    }}
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Reset Tutorial
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            {cognitoClientId && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Session Duration
                  </CardTitle>
                  <CardDescription>Configure how long users stay logged in</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>ID Token (minutes)</Label>
                      <Input
                        type="number"
                        min={5}
                        max={1440}
                        value={sessionSettings.id_token_validity}
                        onChange={(e) => setSessionSettings(prev => ({ ...prev, id_token_validity: parseInt(e.target.value) || 60 }))}
                      />
                      <p className="text-xs text-gray-500">Range: 5-1440 minutes (5 min - 1 day)</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Access Token (minutes)</Label>
                      <Input
                        type="number"
                        min={5}
                        max={1440}
                        value={sessionSettings.access_token_validity}
                        onChange={(e) => setSessionSettings(prev => ({ ...prev, access_token_validity: parseInt(e.target.value) || 60 }))}
                      />
                      <p className="text-xs text-gray-500">Range: 5-1440 minutes (5 min - 1 day)</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Refresh Token (days)</Label>
                      <Input
                        type="number"
                        min={1}
                        max={3650}
                        value={sessionSettings.refresh_token_validity}
                        onChange={(e) => setSessionSettings(prev => ({ ...prev, refresh_token_validity: parseInt(e.target.value) || 30 }))}
                      />
                      <p className="text-xs text-gray-500">Range: 1-3650 days (1 day - 10 years)</p>
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-2">Session Duration Guide</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• <strong>ID Token</strong>: User identity information validity</li>
                      <li>• <strong>Access Token</strong>: API access permission validity</li>
                      <li>• <strong>Refresh Token</strong>: How long users can stay logged in without re-entering password</li>
                      <li>• Shorter durations = more secure, but users must log in more often</li>
                      <li>• Longer durations = more convenient, but less secure</li>
                    </ul>
                  </div>
                  
                  <div className="pt-4 border-t">
                    <Button onClick={saveSessionSettings} disabled={savingSession || loading} size="lg" className="min-w-[180px]">
                      <Save className="h-4 w-4 mr-2" />
                      {savingSession ? 'Saving...' : 'Save Session Settings'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Locations */}
          <TabsContent value="locations" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="h-5 w-5" />
                      Locations
                    </CardTitle>
                    <CardDescription>
                      Manage physical locations ({locations.length}/{maxLocations} used)
                    </CardDescription>
                  </div>
                  <Button onClick={() => { setEditingLocation(null); setShowLocationModal(true) }} disabled={atLocationLimit} size="lg">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Location
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {atLocationLimit && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3 mb-4">
                    <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-yellow-900">Location Limit Reached</h3>
                      <p className="text-sm text-yellow-800 mt-1">
                        You've reached your plan's limit of {maxLocations} location{maxLocations !== 1 ? 's' : ''}. 
                        <a href="/billing" className="underline ml-1">Upgrade your plan</a> to add more.
                      </p>
                    </div>
                  </div>
                )}
                
                {locations.length === 0 ? (
                  <div className="text-center py-12">
                    <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4">No locations configured yet</p>
                    <Button onClick={() => { setEditingLocation(null); setShowLocationModal(true) }}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Your First Location
                    </Button>
                  </div>
                ) : (
                  <DataTable data={locations} columns={locationColumns} searchable searchPlaceholder="Search locations..." />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Services */}
          <TabsContent value="services" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Briefcase className="h-5 w-5" />
                      Services
                    </CardTitle>
                    <CardDescription>
                      Manage service types ({services.length}/{maxServices} used)
                    </CardDescription>
                  </div>
                  <Button onClick={() => { setEditingService(null); setShowServiceModal(true) }} disabled={services.length >= maxServices} size="lg">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Service
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {services.length >= maxServices && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3 mb-4">
                    <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-yellow-900">Service Limit Reached</h3>
                      <p className="text-sm text-yellow-800 mt-1">
                        You've reached your plan's limit of {maxServices} service{maxServices !== 1 ? 's' : ''}. 
                        <a href="/billing" className="underline ml-1">Upgrade your plan</a> to add more.
                      </p>
                    </div>
                  </div>
                )}
                
                {services.length === 0 ? (
                  <div className="text-center py-12">
                    <Briefcase className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4">No services configured yet</p>
                    <Button onClick={() => { setEditingService(null); setShowServiceModal(true) }}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Your First Service
                    </Button>
                  </div>
                ) : (
                  <DataTable data={services} columns={serviceColumns} searchable searchPlaceholder="Search services..." />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Queues */}
          <TabsContent value="queues" className="space-y-6 mt-6">
            <QueueConfigTab 
              locations={locations} 
              services={services} 
              tenantId={activeTenantId!}
              onRefresh={() => { fetchLocations(); fetchServices(); }}
            />
          </TabsContent>

          {/* Branding */}
          <TabsContent value="branding" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="h-5 w-5" />
                  Company Logo
                </CardTitle>
                <CardDescription>Upload and configure your company branding</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Company Name</Label>
                    <Input
                      value={settings.branding?.company_name || ''}
                      onChange={(e) => setSettings((prev: any) => ({
                        ...prev,
                        branding: { ...prev.branding, company_name: e.target.value }
                      }))}
                      placeholder="Your Company Name"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Company Logo</Label>
                    <div className="flex gap-2">
                      <Input
                        value={settings.branding?.logo_url || ''}
                        onChange={(e) => setSettings((prev: any) => ({
                          ...prev,
                          branding: { ...prev.branding, logo_url: e.target.value }
                        }))}
                        placeholder="https://example.com/logo.png"
                        className="flex-1"
                      />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        disabled={loading || saving}
                        className="hidden"
                        id="logo-file"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('logo-file')?.click()}
                        disabled={loading || saving}
                        className="px-3"
                      >
                        <UploadIcon className="h-4 w-4" />
                      </Button>
                    </div>
                    {settings.branding?.logo_url && (
                      <div className="mt-2">
                        <img 
                          src={settings.branding.logo_url} 
                          alt="Logo preview" 
                          className="h-12 w-auto object-contain border rounded"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="h-5 w-5" />
                  Theme & Colors
                </CardTitle>
                <CardDescription>Customize your brand colors and appearance</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-medium flex items-center gap-2">
                      <Sparkles className="h-5 w-5" />
                      Theme Packages
                    </h4>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setCustomMode(!customMode)
                        if (!customMode) setSelectedThemeId(null)
                      }}
                    >
                      {customMode ? 'Use Packages' : 'Custom Colors'}
                    </Button>
                  </div>
                  
                  {!customMode && (
                    <>
                      <div className="flex flex-wrap gap-2">
                        {themeCategories.map((category) => (
                          <Button
                            key={category.id}
                            variant={selectedCategory === category.id ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setSelectedCategory(category.id)}
                          >
                            {category.name}
                          </Button>
                        ))}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {themePackages
                          .filter(theme => theme.category === selectedCategory)
                          .map((theme) => {
                            const isSelected = selectedThemeId === theme.id
                            return (
                              <div
                                key={theme.id}
                                className={`p-3 border rounded-lg cursor-pointer hover:shadow-md transition-all ${
                                  isSelected 
                                    ? 'border-blue-500 bg-blue-50 shadow-md' 
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                                onClick={() => applyThemePackage(theme)}
                              >
                                <div className="flex items-center gap-3 mb-2">
                                  <div className="flex gap-1">
                                    <div 
                                      className="w-4 h-4 rounded-full border" 
                                      style={{ backgroundColor: theme.primary }}
                                    />
                                    <div 
                                      className="w-4 h-4 rounded-full border" 
                                      style={{ backgroundColor: theme.secondary }}
                                    />
                                    <div 
                                      className="w-4 h-4 rounded-full border" 
                                      style={{ backgroundColor: theme.background }}
                                    />
                                  </div>
                                  <div className="flex-1">
                                    <div className={`font-medium text-sm ${
                                      isSelected ? 'text-blue-700' : 'text-gray-900'
                                    }`}>
                                      {theme.name}
                                      {isSelected && <span className="ml-2 text-blue-600">✓</span>}
                                    </div>
                                    <div className="text-xs text-gray-500">{theme.description}</div>
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                      </div>
                    </>
                  )}
                </div>
                
                {customMode && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Primary Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={settings.branding?.primary_color || '#3C226E'}
                          onChange={(e) => setSettings((prev: any) => ({
                            ...prev,
                            branding: { ...prev.branding, primary_color: e.target.value }
                          }))}
                          className="w-16 h-10 p-1 border rounded"
                        />
                        <Input
                          value={settings.branding?.primary_color || '#3C226E'}
                          onChange={(e) => setSettings((prev: any) => ({
                            ...prev,
                            branding: { ...prev.branding, primary_color: e.target.value }
                          }))}
                          placeholder="#3C226E"
                          className="flex-1"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Secondary Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={settings.branding?.secondary_color || '#9CFF00'}
                          onChange={(e) => setSettings((prev: any) => ({
                            ...prev,
                            branding: { ...prev.branding, secondary_color: e.target.value }
                          }))}
                          className="w-16 h-10 p-1 border rounded"
                        />
                        <Input
                          value={settings.branding?.secondary_color || '#9CFF00'}
                          onChange={(e) => setSettings((prev: any) => ({
                            ...prev,
                            branding: { ...prev.branding, secondary_color: e.target.value }
                          }))}
                          placeholder="#9CFF00"
                          className="flex-1"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Admin Background</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={settings.branding?.admin_background_color || '#F5F5F7'}
                          onChange={(e) => setSettings((prev: any) => ({
                            ...prev,
                            branding: { ...prev.branding, admin_background_color: e.target.value }
                          }))}
                          className="w-16 h-10 p-1 border rounded"
                        />
                        <Input
                          value={settings.branding?.admin_background_color || '#F5F5F7'}
                          onChange={(e) => setSettings((prev: any) => ({
                            ...prev,
                            branding: { ...prev.branding, admin_background_color: e.target.value }
                          }))}
                          placeholder="#F5F5F7"
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>SMS Sender Name</Label>
                    <Input
                      value={settings.branding?.sms_sender_name || ''}
                      onChange={(e) => setSettings((prev: any) => ({
                        ...prev,
                        branding: { ...prev.branding, sms_sender_name: e.target.value }
                      }))}
                      placeholder="YourCompany"
                      maxLength={11}
                    />
                    <p className="text-xs text-gray-500">Max 11 characters</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Support Email</Label>
                    <Input
                      type="email"
                      value={settings.branding?.support_email || ''}
                      onChange={(e) => setSettings((prev: any) => ({
                        ...prev,
                        branding: { ...prev.branding, support_email: e.target.value }
                      }))}
                      placeholder="support@yourcompany.com"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Support Phone</Label>
                    <Input
                      value={settings.branding?.support_phone || ''}
                      onChange={(e) => setSettings((prev: any) => ({
                        ...prev,
                        branding: { ...prev.branding, support_phone: e.target.value }
                      }))}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h4 className="text-lg font-medium mb-4">Web Join Page Customization</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Page Title</Label>
                      <Input
                        value={settings.branding?.web_join_title || ''}
                        onChange={(e) => setSettings((prev: any) => ({
                          ...prev,
                          branding: { ...prev.branding, web_join_title: e.target.value }
                        }))}
                        placeholder="Join Our Queue"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Page Subtitle</Label>
                      <Input
                        value={settings.branding?.web_join_subtitle || ''}
                        onChange={(e) => setSettings((prev: any) => ({
                          ...prev,
                          branding: { ...prev.branding, web_join_subtitle: e.target.value }
                        }))}
                        placeholder="Please fill out the form below"
                      />
                    </div>
                    
                    <div className="space-y-2 md:col-span-2">
                      <Label>Footer Text</Label>
                      <Input
                        value={settings.branding?.web_join_footer || ''}
                        onChange={(e) => setSettings((prev: any) => ({
                          ...prev,
                          branding: { ...prev.branding, web_join_footer: e.target.value }
                        }))}
                        placeholder="Thank you for choosing our service"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Background Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={settings.branding?.web_join_background_color || '#FFFFFF'}
                          onChange={(e) => setSettings((prev: any) => ({
                            ...prev,
                            branding: { ...prev.branding, web_join_background_color: e.target.value }
                          }))}
                          className="w-16 h-10 p-1 border rounded"
                        />
                        <Input
                          value={settings.branding?.web_join_background_color || '#FFFFFF'}
                          onChange={(e) => setSettings((prev: any) => ({
                            ...prev,
                            branding: { ...prev.branding, web_join_background_color: e.target.value }
                          }))}
                          placeholder="#FFFFFF"
                          className="flex-1"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Text Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={settings.branding?.web_join_text_color || '#333333'}
                          onChange={(e) => setSettings((prev: any) => ({
                            ...prev,
                            branding: { ...prev.branding, web_join_text_color: e.target.value }
                          }))}
                          className="w-16 h-10 p-1 border rounded"
                        />
                        <Input
                          value={settings.branding?.web_join_text_color || '#333333'}
                          onChange={(e) => setSettings((prev: any) => ({
                            ...prev,
                            branding: { ...prev.branding, web_join_text_color: e.target.value }
                          }))}
                          placeholder="#333333"
                          className="flex-1"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Button Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={settings.branding?.web_join_button_color || '#3C226E'}
                          onChange={(e) => setSettings((prev: any) => ({
                            ...prev,
                            branding: { ...prev.branding, web_join_button_color: e.target.value }
                          }))}
                          className="w-16 h-10 p-1 border rounded"
                        />
                        <Input
                          value={settings.branding?.web_join_button_color || '#3C226E'}
                          onChange={(e) => setSettings((prev: any) => ({
                            ...prev,
                            branding: { ...prev.branding, web_join_button_color: e.target.value }
                          }))}
                          placeholder="#3C226E"
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="pt-6 border-t">
                  <div className="flex gap-3">
                    <Button onClick={saveSettings} disabled={saving || loading} size="lg" className="min-w-[160px]">
                      <Save className="h-4 w-4 mr-2" />
                      {saving ? 'Saving...' : 'Save All Branding'}
                    </Button>
                    
                    <Button 
                      variant="outline"
                      size="lg"
                      onClick={() => window.open(`/join?tenant=${activeTenantId}&location_id=PREVIEW&service_id=PREVIEW`, '_blank')}
                      disabled={!activeTenantId}
                    >
                      <Palette className="h-4 w-4 mr-2" />
                      Preview Join Page
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <LocationModal
        isOpen={showLocationModal}
        onClose={() => { setShowLocationModal(false); setEditingLocation(null) }}
        location={editingLocation}
        tenantId={activeTenantId!}
        onSuccess={() => { fetchLocations(); setShowLocationModal(false); setEditingLocation(null) }}
      />

      <ServiceModal
        isOpen={showServiceModal}
        onClose={() => { setShowServiceModal(false); setEditingService(null) }}
        service={editingService}
        tenantId={activeTenantId!}
        onSuccess={() => { fetchServices(); setShowServiceModal(false); setEditingService(null) }}
      />
    </TenantGuard>
  )
}
